import { z } from 'zod';

/**
 * Money is always integer minor units plus an ISO-4217 code (§4.2). Floats never appear:
 * a 100 € welcome bonus rendered as 99.99999 € on one of 5 214 sites is a support ticket
 * nobody can reproduce.
 */

export const currencyCodeSchema = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/u, 'ISO-4217 uppercase code');

export const moneySchema = z.object({
  /** Minor units — cents, satoshi-free integers. */
  amount: z.number().int(),
  currency: currencyCodeSchema,
});
export type Money = z.infer<typeof moneySchema>;

/** Currencies whose minor unit is not 1/100. Keeps rendering honest for JPY & friends. */
const MINOR_UNIT_EXPONENT: Record<string, number> = {
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
  ISK: 0,
  HUF: 0,
  BHD: 3,
  KWD: 3,
  OMR: 3,
  TND: 3,
};

export function minorUnitExponent(currency: string): number {
  return MINOR_UNIT_EXPONENT[currency] ?? 2;
}

export function minorUnitFactor(currency: string): number {
  return 10 ** minorUnitExponent(currency);
}

export function money(amount: number, currency: string): Money {
  return { amount: Math.round(amount), currency };
}

export function fromMajor(major: number, currency: string): Money {
  return { amount: Math.round(major * minorUnitFactor(currency)), currency };
}

export function toMajor(m: Money): number {
  return m.amount / minorUnitFactor(m.currency);
}

/**
 * "Nice" rounding from the geo preset: marketing copy says «100 €», never «97,43 €».
 * Rounding is applied in MAJOR units — a step of 10 means 10 euro, not 10 cents.
 */
export const ROUNDING_RULES = ['none', 'nice_10', 'nice_50', 'nice_100'] as const;
export type RoundingRule = (typeof ROUNDING_RULES)[number];

const RULE_STEP: Record<RoundingRule, number> = {
  none: 0,
  nice_10: 10,
  nice_50: 50,
  nice_100: 100,
};

/**
 * Rounds to the nearest step, but never down to zero for a positive amount — a bonus of
 * 4 € must not become "0 €"; it becomes one step.
 */
export function applyRounding(m: Money, rule: RoundingRule): Money {
  const step = RULE_STEP[rule];
  if (step === 0) return m;

  const factor = minorUnitFactor(m.currency);
  const stepMinor = step * factor;
  const sign = m.amount < 0 ? -1 : 1;
  const abs = Math.abs(m.amount);

  let rounded = Math.round(abs / stepMinor) * stepMinor;
  if (rounded === 0 && abs > 0) rounded = stepMinor;

  return { amount: sign * rounded, currency: m.currency };
}

/**
 * Converts through an explicit rate (quote per 1 base). Rates are stored as integers
 * scaled by {@link RATE_SCALE} so no float ever touches a stored amount.
 */
export const RATE_SCALE = 1_000_000;

export function convert(m: Money, toCurrency: string, scaledRate: number): Money {
  if (m.currency === toCurrency) return m;
  const fromFactor = minorUnitFactor(m.currency);
  const toFactor = minorUnitFactor(toCurrency);
  // amount_minor / fromFactor * rate * toFactor, all in one expression to round once.
  const converted = (m.amount * scaledRate * toFactor) / (RATE_SCALE * fromFactor);
  return { amount: Math.round(converted), currency: toCurrency };
}

/**
 * Renders using the geo preset's `currency_format` template, e.g. `'{amount} €'` or
 * `'$ {amount}'`. Number grouping follows the preset's `number_format`.
 */
export function formatMoney(
  m: Money,
  options?: { template?: string; locale?: string; maximumFractionDigits?: number },
): string {
  const exp = minorUnitExponent(m.currency);
  const major = toMajor(m);
  const digits = options?.maximumFractionDigits ?? (Number.isInteger(major) ? 0 : exp);
  const formatted = new Intl.NumberFormat(options?.locale ?? 'uk-UA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(major);
  const template = options?.template ?? '{amount} {currency}';
  return template.replace('{amount}', formatted).replace('{currency}', m.currency);
}
